/**
 * Shiki transformer that detects component/composable names in code
 * and wraps them with data attributes for hover popovers.
 *
 * Marks potential API references at build time. Client-side component
 * validates against actual API data and handles display.
 *
 * Component and composable whitelists are auto-generated from source
 * directories by generate-api-whitelist.ts at build time.
 */

// Auto-generated whitelists from packages/0/src/
import { V0_COMPONENTS, V0_COMPOSABLES, V0_COMPOSABLE_TO_DIR } from './generated/api-whitelist'
// Vue API names only — the heavy content in vue-api-content.ts stays
// dynamically imported (DocsApiHover, hover-time) so it doesn't ride along in
// the client bundle this transformer is part of.
import { VUE_API_NAMES as VUE_API_NAME_LIST } from './vue-api-names'

// Types
import type { ShikiTransformer } from 'shiki'

// Component pattern: Namespace.Part (e.g., Popover.Root, ExpansionPanel.Group)
// Tokens come through as combined strings like "Popover.Root"
const COMPONENT_PATTERN = /^([A-Z][a-zA-Z]*)(?:\.[A-Z][a-zA-Z]*)?$/

// Trinity return values that map to their factory function
// createContext returns [useContext, provideContext]
// createPlugin returns [usePlugin, Plugin]
const TRINITY_RETURNS: Record<string, string> = {
  useContext: 'createContext',
  provideContext: 'createContext',
}

// Vue API names derived from vue-api-content.ts
// Used for build-time detection of Vue functions in code
const VUE_API_NAMES = new Set<string>(VUE_API_NAME_LIST)

/**
 * Maps composable names to their canonical API page (directory name).
 * Returns the API name if valid, null otherwise.
 *
 * Uses the V0_COMPOSABLE_TO_DIR mapping generated from source directories.
 * Each function name maps to its containing directory, which is the API cache key.
 *
 * Examples:
 * - createStackPlugin -> useStack (the directory containing it)
 * - createStackContext -> useStack
 * - useStack -> useStack
 * - createSelection -> createSelection
 * - useContext -> createContext (trinity return value)
 */
function resolveComposable (name: string): { apiName: string } | null {
  // Check special trinity return values first (e.g., useContext -> createContext)
  const trinityParent = TRINITY_RETURNS[name]
  if (trinityParent) {
    return { apiName: trinityParent }
  }

  // Not in whitelist at all? Skip.
  if (!V0_COMPOSABLES.has(name)) {
    return null
  }

  // Use the mapping to get the directory name (API cache key)
  const dirName = V0_COMPOSABLE_TO_DIR[name]
  if (dirName) {
    return { apiName: dirName }
  }

  // Fallback: use the name as-is (shouldn't happen if whitelist is in sync)
  return { apiName: name }
}

/**
 * Creates a Shiki transformer that marks potential API tokens.
 *
 * Components: Detects "Namespace.Part" patterns (e.g., Popover.Root)
 * Composables: Detects use* or create* patterns
 * Vue built-ins: Links to Vue documentation
 *
 * Client-side validation happens in DocsApiHover.vue
 */
export function createApiTransformer (): ShikiTransformer {
  return {
    name: 'api-hover',

    span (node) {
      // Only process text nodes within spans (tokens)
      if (!node.children || node.children.length !== 1) return
      const child = node.children[0]
      if (child.type !== 'text') return

      const text = child.value
      if (!text) return

      const trimmed = text.trim()
      if (!trimmed) return

      // Determine if this is an API token (v0 component, v0 composable, or Vue API)
      let apiType: 'component' | 'composable' | 'vue' | null = null
      let apiName = trimmed

      // Check if it's a v0 composable (whitelist-based)
      const composable = resolveComposable(trimmed)
      if (composable) {
        apiType = 'composable'
        apiName = composable.apiName
      } else {
        // Check if it's a v0 component (namespace must be in whitelist)
        const match = COMPONENT_PATTERN.exec(trimmed)
        if (match && V0_COMPONENTS.has(match[1])) {
          apiType = 'component'
        } else if (VUE_API_NAMES.has(trimmed)) {
          // Check if it's a Vue API
          apiType = 'vue'
        }
      }

      // Nothing to mark
      if (!apiType) return

      // Extract leading/trailing whitespace
      const leadingWs = text.match(/^\s*/)?.[0] || ''
      const trailingWs = text.match(/\s*$/)?.[0] || ''

      // Build properties - all types now use data-api-candidate pattern
      const properties: Record<string, string> = {
        'data-api-candidate': trimmed,
        'data-api-name': apiName,
        'data-api-type': apiType,
      }

      // If no whitespace, simple case - just add attributes
      if (!leadingWs && !trailingWs) {
        node.properties = node.properties || {}
        Object.assign(node.properties, properties)
        return
      }

      // Has whitespace - restructure node to separate whitespace from API token
      // Create new children: [leading ws text] + [api span] + [trailing ws text]
      const newChildren: typeof node.children = []

      if (leadingWs) {
        newChildren.push({ type: 'text', value: leadingWs })
      }

      // Create inner span for the token
      newChildren.push({
        type: 'element',
        tagName: 'span',
        properties,
        children: [{ type: 'text', value: trimmed }],
      })

      if (trailingWs) {
        newChildren.push({ type: 'text', value: trailingWs })
      }

      node.children = newChildren
    },
  }
}

/**
 * Renders inline code for Vue API references.
 * Returns HTML string if text is a Vue API, null otherwise.
 */
export function renderVueApiInlineCode (
  text: string,
  escapeHtml: (s: string) => string,
): string | null {
  if (!VUE_API_NAMES.has(text)) return null
  const escaped = escapeHtml(text)
  return `<code data-api-candidate="${escaped}" data-api-name="${escaped}" data-api-type="vue">${escaped}</code>`
}

/**
 * Renders inline code for v0 API references (components and composables).
 * Returns HTML string if text is a v0 API, null otherwise.
 */
export function renderV0ApiInlineCode (
  text: string,
  escapeHtml: (s: string) => string,
): string | null {
  const composable = resolveComposable(text)
  if (composable) {
    const escaped = escapeHtml(text)
    const apiName = escapeHtml(composable.apiName)
    return `<code data-api-candidate="${escaped}" data-api-name="${apiName}" data-api-type="composable">${escaped}</code>`
  }

  const match = COMPONENT_PATTERN.exec(text)
  if (match && V0_COMPONENTS.has(match[1])) {
    const escaped = escapeHtml(text)
    return `<code data-api-candidate="${escaped}" data-api-name="${escaped}" data-api-type="component">${escaped}</code>`
  }

  return null
}

// Export for use in markdown.ts inline code processing
export { VUE_API_NAMES }
